/**
 * CartBell — "Pedido abierto" del vendedor: ícono de carrito con badge de
 * cantidad + panel desplegable (mismo patrón que NotificationBell). Muestra los
 * productos, cantidades, neto/impuesto/total, y las salidas del carrito.
 *
 * Dos salidas, a propósito:
 *  - PDV-01 «Boleta» / «Factura»: venta de mostrador, emite en un paso. Requiere
 *    el módulo `sales.pos`, que existe justamente para no tener que darle al
 *    vendedor `sales.documents: approve` — eso lo habilitaría a aprobar cualquier
 *    documento del tenant, incluida una factura B2B ajena.
 *  - «Ir al pedido»: el flujo B2B de siempre, donde otra persona aprueba. Se
 *    mantiene porque una venta negociada sí debe pasar por esa separación.
 */
import { useMemo, useState, useRef } from 'react'
import { useCierreAlClicAfuera } from '@/lib/useCierreAlClicAfuera'
import { useNavigate } from 'react-router-dom'
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, X, Search, Loader2, Receipt, FileText } from 'lucide-react'
import { useTenantTax } from '@/modules/config/hooks/useConfig'
import { usePermission } from '@/hooks/usePermission'
import { useCustomers } from '@/modules/sales/hooks/useSales'
import { usePosCheckout, usePosDefaultCustomer, cartToPosLines } from '@/modules/sales/hooks/usePos'
import {
  useCartStore, cartItemCount, cartTotals, cartToOrderLines,
} from '@/store/cartStore'

const fmtCLP = (n: number) => '$' + Math.round(n).toLocaleString('es-CL')

export function CartBell() {
  const [open, setOpen] = useState(false)
  const disparadorRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  useCierreAlClicAfuera(open, [disparadorRef, panelRef], () => setOpen(false))
  const navigate = useNavigate()
  const items = useCartStore(s => s.items)
  const setQty = useCartStore(s => s.setQty)
  const removeItem = useCartStore(s => s.removeItem)
  const clear = useCartStore(s => s.clear)

  const tax = useTenantTax()
  const taxRate = tax.rate
  const { can } = usePermission()
  const puedeVenderEnMostrador = can('sales.pos', 'create')

  const count = cartItemCount(items)
  const { neto, iva, total } = cartTotals(items, taxRate)

  // ── Venta de mostrador ──
  const [buscandoCliente, setBuscandoCliente] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [cliente, setCliente] = useState<{ id: string; legal_name: string; tax_id: string } | null>(null)
  const [error, setError] = useState('')

  // El receptor genérico se precarga: en el mostrador la boleta sin datos del
  // comprador es el caso normal, no la excepción.
  const { data: clienteBoleta } = usePosDefaultCustomer(open && puedeVenderEnMostrador)
  const receptor = cliente ?? clienteBoleta ?? null
  const esReceptorGenerico = !cliente

  const { data: clientes, isFetching } = useCustomers(
    buscandoCliente && busqueda.trim().length >= 2
      ? { search: busqueda.trim(), active: 'true', page_size: 6 }
      : undefined,
  )
  const resultados = useMemo(
    () => (buscandoCliente && busqueda.trim().length >= 2 ? clientes?.customers ?? [] : []),
    [buscandoCliente, busqueda, clientes],
  )

  const checkout = usePosCheckout()

  const vender = (tipo: 'BE' | 'FE') => {
    setError('')
    // Una factura al receptor genérico no identifica al comprador: el SII la
    // acepta pero el cliente no puede usarla como crédito fiscal.
    if (tipo === 'FE' && esReceptorGenerico) {
      setError('Para facturar hay que elegir el cliente.')
      setBuscandoCliente(true)
      return
    }
    checkout.mutate(
      {
        document_type: tipo,
        customer_id: cliente?.id,
        lines: cartToPosLines(items),
      },
      {
        onSuccess: (r) => {
          clear()
          setCliente(null)
          setBuscandoCliente(false)
          setBusqueda('')
          setOpen(false)
          navigate(`/sales/documents/${r.document_id}`)
        },
        onError: (e) => {
          const err = e as { response?: { data?: { error?: { message?: string } } } }
          setError(err.response?.data?.error?.message ?? 'No se pudo registrar la venta.')
        },
      },
    )
  }

  const goToOrder = () => {
    setOpen(false)
    navigate('/sales/orders/new', { state: { cartLines: cartToOrderLines(items) } })
  }

  return (
    <div className="relative">
      <button ref={disparadorRef}
        onClick={() => setOpen(p => !p)}
        className="relative p-2 text-content-muted hover:text-content hover:bg-soft rounded-lg transition-colors"
        title="Pedido abierto"
        aria-label={count > 0 ? `Pedido abierto (${count} ítems)` : 'Pedido abierto'}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <ShoppingCart size={18} />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center text-[10px] font-bold bg-primary text-on-primary rounded-full px-0.5">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <div ref={panelRef}
            className="absolute right-0 top-11 z-50 w-80 bg-popover border border-divider rounded-xl shadow-lg overflow-hidden"
            style={{ backgroundColor: 'rgb(var(--color-popover))' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-divider">
              <p className="text-sm font-semibold text-content">Pedido abierto</p>
              {items.length > 0 && (
                <button onClick={clear}
                  className="inline-flex items-center gap-1 text-xs text-content-muted hover:text-danger-content-soft transition-colors">
                  <X size={13} />Limpiar
                </button>
              )}
            </div>

            {/* Líneas */}
            <div className="max-h-80 overflow-y-auto">
              {items.length === 0 ? (
                <div className="py-8 text-center text-sm text-content-subtle">
                  <ShoppingCart size={24} className="mx-auto mb-2 opacity-30" />
                  Sin productos. Agrégalos desde el catálogo.
                </div>
              ) : (
                <div className="divide-y divide-divider">
                  {items.map(i => {
                    const lineTotal = parseFloat(i.unit_price || '0') * i.quantity
                    return (
                      <div key={i.product_id} className="flex gap-2 px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-content leading-snug truncate" title={i.name}>{i.name}</p>
                          <p className="text-[10px] text-content-subtle font-mono">{i.sku}</p>
                          <div className="mt-1.5 flex items-center gap-1.5">
                            <button onClick={() => setQty(i.product_id, i.quantity - 1)}
                              className="w-5 h-5 flex items-center justify-center rounded border border-divider text-content-muted hover:bg-soft" aria-label="Restar">
                              <Minus size={11} />
                            </button>
                            <span className="text-xs font-medium text-content w-6 text-center tabular-nums">{i.quantity}</span>
                            <button onClick={() => setQty(i.product_id, i.quantity + 1)}
                              className="w-5 h-5 flex items-center justify-center rounded border border-divider text-content-muted hover:bg-soft" aria-label="Sumar">
                              <Plus size={11} />
                            </button>
                            <span className="ml-auto text-xs font-medium text-content tabular-nums">{fmtCLP(lineTotal)}</span>
                          </div>
                        </div>
                        <button onClick={() => removeItem(i.product_id)}
                          className="shrink-0 self-start p-1 rounded text-content-subtle hover:text-danger-content-soft transition-colors" title="Quitar">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Totales + acciones */}
            {items.length > 0 && (
              <div className="border-t border-divider px-4 py-3 space-y-1.5">
                <div className="flex justify-between text-xs text-content-muted">
                  <span>Neto</span><span className="tabular-nums">{fmtCLP(neto)}</span>
                </div>
                <div className="flex justify-between text-xs text-content-muted">
                  <span>{tax.name} ({taxRate}%)</span><span className="tabular-nums">{fmtCLP(iva)}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold text-content pt-1 border-t border-divider">
                  <span>Total</span><span className="tabular-nums">{fmtCLP(total)}</span>
                </div>

                {puedeVenderEnMostrador && (
                  <div className="pt-2 space-y-2">
                    {/* Receptor */}
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-content-subtle shrink-0">Cliente</span>
                      <button
                        onClick={() => setBuscandoCliente(v => !v)}
                        className="flex-1 min-w-0 text-left px-2 py-1 rounded border border-divider hover:bg-soft transition-colors"
                        title={receptor ? `${receptor.legal_name} · ${receptor.tax_id}` : 'Elegir cliente'}
                      >
                        <span className="block truncate text-content">
                          {receptor ? receptor.legal_name : 'Cargando…'}
                        </span>
                      </button>
                      {cliente && (
                        <button onClick={() => setCliente(null)} title="Volver al receptor genérico"
                          className="shrink-0 p-1 rounded text-content-subtle hover:text-content">
                          <X size={12} />
                        </button>
                      )}
                    </div>

                    {buscandoCliente && (
                      <div className="rounded-lg border border-divider overflow-hidden">
                        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-divider">
                          <Search size={12} className="text-content-subtle shrink-0" />
                          <input aria-label="Buscar producto"
                            autoFocus
                            value={busqueda}
                            onChange={e => setBusqueda(e.target.value)}
                            placeholder="Nombre o RUT…"
                            className="w-full bg-transparent text-xs text-content outline-none placeholder:text-content-subtle"
                          />
                          {isFetching && <Loader2 size={12} className="animate-spin text-content-subtle" />}
                        </div>
                        <div className="max-h-32 overflow-y-auto">
                          {resultados.length === 0 ? (
                            <p className="px-2 py-2 text-[11px] text-content-subtle">
                              {busqueda.trim().length < 2 ? 'Escribir al menos 2 letras.' : 'Sin resultados.'}
                            </p>
                          ) : resultados.map(c => (
                            <button
                              key={c.id}
                              onClick={() => {
                                setCliente({ id: c.id, legal_name: c.legal_name, tax_id: c.tax_id })
                                setBuscandoCliente(false)
                                setBusqueda('')
                                setError('')
                              }}
                              className="w-full text-left px-2 py-1.5 hover:bg-soft transition-colors"
                            >
                              <span className="block text-xs text-content truncate">{c.legal_name}</span>
                              <span className="block text-[10px] text-content-subtle font-mono">{c.tax_id}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {error && (
                      <p role="alert" className="text-[11px] text-danger-content-soft">{error}</p>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => vender('BE')}
                        disabled={checkout.isPending}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors"
                      >
                        {checkout.isPending ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />}
                        Boleta
                      </button>
                      <button
                        onClick={() => vender('FE')}
                        disabled={checkout.isPending}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-primary/40 text-primary text-sm font-medium rounded-lg hover:bg-primary-50 disabled:opacity-60 transition-colors"
                      >
                        {checkout.isPending ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                        Factura
                      </button>
                    </div>
                  </div>
                )}

                <button onClick={goToOrder}
                  className={puedeVenderEnMostrador
                    ? 'mt-1 w-full inline-flex items-center justify-center gap-1.5 text-xs text-content-muted hover:text-primary transition-colors'
                    : 'mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors'}>
                  Ir al pedido <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
